var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var HistogramVisualParams_1;
import { JSON, JsonSchema, JsonDiscriminatorValue } from '@symon-ai/wisepipe-serialize';
import { VisualParams } from './visualParams';
import { VisualType } from '@symon-ai/wisepipe-base-computation';
import { AggregateColumn } from '@symon-ai/wisepipe-base-pipe';
import { SortColumn } from '@symon-ai/wisepipe-common-pipe';
import { squel } from '@symon-ai/wisepipe-squel';
const schema = {
    $id: 'urn:wisepipe-common-computation:HistogramVisualParams',
    type: 'object',
    properties: {
        xAxis: { type: 'string', minLength: 1 },
        numBins: { type: 'integer', minimum: 1, maximum: 100 }
    },
    required: ['xAxis']
};
let HistogramVisualParams = HistogramVisualParams_1 = class HistogramVisualParams extends VisualParams {
    constructor(args) {
        super({
            ...args,
            type: VisualType.Histogram
        });
        this.xAxis = args.xAxis;
        this.numBins = args.numBins;
        JSON.validate(this, HistogramVisualParams_1);
    }
    static get xAxisAlias() {
        return 'xAxis';
    }
    static get valueAlias() {
        return 'value';
    }
    getFields() {
        return [
            { source: this.xAxis, alias: HistogramVisualParams_1.xAxisAlias, numBins: this.numBins },
            {
                source: this.xAxis,
                alias: HistogramVisualParams_1.valueAlias,
                aggOp: AggregateColumn.Operation.COUNT
            }
        ];
    }
    setFinalQuery(query, type, tableName) {
        // Example of the final query:
        //
        // WITH infoQuery AS (
        // 	SELECT MAX("Payrate") AS "maxVal",
        // 		MIN("Payrate") AS "minVal",
        // 		CAST(
        // 			least(
        // 				round(MAX("Payrate")) - round(MIN("Payrate")),
        // 				100,
        // 				27
        // 			) AS INT
        // 		) AS "numBins"
        // 	FROM "f8f40d7a-4059-4b1d-b6b3-2921b06ffb4a/0EcQDh4W1lMZtQ/pr60jL73UjoMdA/default"
        // ),
        // sequenceQuery AS (
        // 	SELECT slice(
        // 			sequence(
        // 				CAST("minVal" AS BIGINT) * 100,
        // 				CAST("maxVal" AS BIGINT) * 100,
        // 				CAST(
        // 					(
        // 						CAST("maxVal" AS DECIMAL(18,2)) - CAST("minVal" AS DECIMAL(18,2))
        // 					) / "numBins" * 100 AS BIGINT
        // 				)
        // 			),
        // 			1,
        // 			"numBins"
        // 		) AS "__sequence"
        // 	FROM infoQuery
        // ),
        // rangeQuery AS (
        // 	SELECT "__range", "__label"
        // 	 FROM
        // 	 (
        // 			SELECT ngrams(
        // 					transform(__sequence, ele->cast(ele AS DECIMAL(18,2)) / 100 ) || 2147483647,
        // 					2
        // 				) AS "__rangeArray"
        // 			FROM "sequenceQuery"
        // 		)
        // 		CROSS JOIN UNNEST("__rangeArray") AS t("__range"),
        // 		(
        // 			SELECT ngrams(
        // 					transform(
        // 						transform(
        // 							__sequence,
        // 							ele->round(cast(ele AS DECIMAL(18,1)) / 100)
        // 						) || cast(round("maxVal") AS DECIMAL(18,1)),
        // 						ele->CAST(ele AS VARCHAR)
        // 					),
        // 					2
        // 				) AS "__labelArray"
        // 			FROM "sequenceQuery",
        // 				"infoQuery"
        // 		)
        // 		CROSS JOIN UNNEST("__labelArray") AS t("__label")
        // 	WHERE (
        // 			CAST(CAST(round("__range" [ 1 ]) AS DECIMAL(18,1)) AS VARCHAR) = "__label" [ 1 ]
        // 		)
        // )
        // select * from rangeQuery
        // SELECT "__label" [ 1 ] || ' to ' || "__label" [ 2 ] AS xAxis,
        // 	coalesce(sum("value"), 0) AS value
        // FROM (
        // 		SELECT "EmpID" AS "xAxis",
        // 			(cast(COUNT("EmpID") as int)) AS "value"
        // 		FROM (
        // 				SELECT "PayRate" AS "PayRate"
        // 				FROM "f8f40d7a-4059-4b1d-b6b3-2921b06ffb4a/0EcQDh4W1lMZtQ/pr60jL73UjoMdA/default"
        // 			)
        // 		GROUP BY "EmpID"
        // 		ORDER BY "xAxis" ASC
        // 	)
        // 	RIGHT JOIN rangeQuery ON (
        // 		"xAxis" >= "__range" [ 1 ]
        // 		AND "xAxis" < "__range" [ 2 ]
        // 	)
        // GROUP BY "__range",
        // 	"__label"
        // ORDER BY "__range" ASC
        if (this.numBins) {
            const finalQuery = squel.useFlavour('postgres').select({ autoQuoteFieldNames: false }).from(query);
            // infoQuery contains a max and min value of the column for binning
            const infoQuery = squel.select({ autoQuoteFieldNames: false }).from(tableName);
            infoQuery.field(`MAX("${this.xAxis}")`, 'maxVal').field(`MIN("${this.xAxis}")`, 'minVal');
            if (type === 'date') {
                infoQuery.field(`CAST(least(date_diff('day',MIN("${this.xAxis}"), MAX("${this.xAxis}")), 100, ${this.numBins})AS INT)`, 'numBins');
            }
            else {
                infoQuery.field(`CAST(least(round(MAX("${this.xAxis}")) - round(MIN("${this.xAxis}")), 100, ${this.numBins})  AS INT)`, 'numBins');
            }
            finalQuery.with('infoQuery', infoQuery);
            // rangeQuery will return a table with a column of a [start, end] array.
            // if maxVal = 10, minVal = 1, numBin = 3
            // sequence(start, end, step) returns a constant sequence array from start to end by step. (sequence(1,10,(10 - 1)/ 3) -> [1 4 7 10])
            //   - sometimes, if the range is not perfect, the last value will not be included in the label, therefore we will slice the end and replace with maxVal
            // ngrams( array , size ) returns array that groups neighbor element into size. -> ngrams(sequence(1,10,(10 - 1)/ 3), 2) -> [[1 4] [4 7] [7 10]]
            // UNNEST function simply converts an array into columns
            // We can use ngrams and sequence to find the range of the columns by right joining the visualQuery with our range & label.
            // Because the histogram grouping is left closed intervals, we need to trim the last element of the range and append maximum value.
            const sequenceQuery = squel.select({ autoQuoteFieldNames: false, autoQuoteTableNames: false }).from('infoQuery');
            finalQuery.with('sequenceQuery', sequenceQuery);
            const rangeQuery = squel
                .select({ autoQuoteFieldNames: false, autoQuoteTableNames: false })
                .field('"__range"')
                .field(`"__label"`);
            if (type === 'date') {
                sequenceQuery.field(`slice( sequence("minVal", "maxVal", ("maxVal" - "minVal") / "numBins"),1,"numBins")`, '__sequence');
                rangeQuery.from(`(SELECT ngrams( "__sequence" || CAST('2262-04-11 23:47:16' AS timestamp),2) AS "__rangeArray" FROM "sequenceQuery") CROSS JOIN UNNEST("__rangeArray") AS t("__range")`);
                rangeQuery.from(`(SELECT ngrams( transform( __sequence, ele -> CAST(CAST(ele AS DATE) AS VARCHAR)) || cast(cast("maxVal" as date) as varchar),2) AS "__labelArray" FROM "sequenceQuery", "infoQuery") CROSS JOIN UNNEST("__labelArray") AS t("__label")`);
                rangeQuery.where(`CAST(CAST("__range"[1] AS DATE) AS VARCHAR) = "__label"[1]`);
            }
            else {
                sequenceQuery.field(`slice( sequence(CAST("minVal" AS BIGINT) * 100, CAST("maxVal" AS BIGINT) * 100, CAST((CAST("maxVal" AS DECIMAL(18,2)) - CAST("minVal" AS DECIMAL(18,2))) / "numBins" * 100 AS BIGINT)),1,"numBins")`, '__sequence');
                // Range
                rangeQuery.from(`(SELECT ngrams( transform( __sequence , ele -> cast(ele AS DECIMAL(18,2)) / 100) || 2147483647,2) AS "__rangeArray" FROM "sequenceQuery") CROSS JOIN UNNEST("__rangeArray") AS t("__range")`);
                // Label
                rangeQuery.from(`(SELECT ngrams( transform( transform( __sequence , ele -> round(cast(ele AS DECIMAL(18,1)) / 100)) || cast(round("maxVal") AS DECIMAL(18,1)),ele -> CAST(ele AS VARCHAR)),2) AS "__labelArray" FROM "sequenceQuery", "infoQuery") CROSS JOIN UNNEST("__labelArray") AS t("__label")`);
                rangeQuery.where(`CAST(round(round("__range"[1], 1)) AS VARCHAR) || '.0' = "__label"[1]`);
            }
            finalQuery.with('rangeQuery', rangeQuery);
            finalQuery.field(`"__label"[1] || ' to ' || "__label"[2]`, HistogramVisualParams_1.xAxisAlias);
            finalQuery.field(`coalesce(sum("${HistogramVisualParams_1.valueAlias}"), 0)`, HistogramVisualParams_1.valueAlias);
            finalQuery.right_join('rangeQuery', undefined, `"${HistogramVisualParams_1.xAxisAlias}" >= "__range"[1] AND "${HistogramVisualParams_1.xAxisAlias}" < "__range"[2]`);
            finalQuery.group(`"__range"`);
            finalQuery.group(`"__label"`);
            finalQuery.order(`"__range"`, this.sortDir === SortColumn.SortDirection.ASCENDING);
            return finalQuery;
        }
        return query;
    }
};
HistogramVisualParams = HistogramVisualParams_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(VisualType.Histogram)
], HistogramVisualParams);
export { HistogramVisualParams };
//# sourceMappingURL=histogramVisualParams.js.map